import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'

/**
 * Realtime slice (PX-W6.1) — the websocket skeleton's write target.
 *
 * Two write shapes only, per the realtime canon (no delta-merge of server
 * content, I-9):
 *  - INVALIDATION nonces: a frame about an entity family bumps that family's
 *    nonce; mounted list surfaces refetch when their nonce moves.
 *  - SHELL upserts: a frame for a specific id upserts a SHELL (create-on-
 *    unknown, AC-10) — a refetch signal keyed by id, never merged server
 *    content.
 *  - ECHO accounting: a frame the local user caused themselves is counted,
 *    never re-applied on top of their own thunk result (I-7).
 *
 * Every reducer is pure (I-8) — the store-audit test enforces it.
 */

/** A refetch signal for one concrete id (never merged server content). */
export interface RealtimeShell {
  /** The map key, e.g. `notification:<id>`. */
  id: string
  /** The entity family the shell belongs to (e.g. `notification`). */
  kind: string
  /** Epoch millis when the frame arrived. */
  receivedAt: number
  /** The frame's own payload, kept opaque for later waves to refetch against. */
  payload: Record<string, unknown>
}

/** One invalidation nonce per refetching surface family. */
export interface RealtimeNonces {
  board: number
  projects: number
  versions: number
  activity: number
  notifications: number
}

export interface RealtimeState {
  nonces: RealtimeNonces
  entities: Record<string, RealtimeShell>
  /** How many inbound frames were echo-suppressed (caused by this user). */
  echoes: number
}

const initialState: RealtimeState = {
  nonces: { board: 0, projects: 0, versions: 0, activity: 0, notifications: 0 },
  entities: {},
  echoes: 0
}

/** Bump exactly one nonce (shared shape for the five invalidate actions). */
const bump = (key: keyof RealtimeNonces) => (state: RealtimeState) => {
  state.nonces[key] += 1
}

export const realtimeSlice = createSlice({
  name: 'realtime',
  initialState,
  reducers: {
    invalidateBoard: bump('board'),
    invalidateProjects: bump('projects'),
    invalidateVersions: bump('versions'),
    invalidateActivity: bump('activity'),
    invalidateNotifications: bump('notifications'),
    /**
     * Upsert a shell for a concrete id. Create-on-unknown (AC-10) AND
     * overwrite-on-known: a shell is a refetch signal, not merged data, so
     * overwriting loses nothing (I-9).
     */
    shellUpserted(state, action: PayloadAction<RealtimeShell>) {
      state.entities[action.payload.id] = action.payload
    },
    /** Count an echo-suppressed frame (I-7). */
    echoSuppressed(state) {
      state.echoes += 1
    }
  }
})

export const {
  invalidateBoard,
  invalidateProjects,
  invalidateVersions,
  invalidateActivity,
  invalidateNotifications,
  shellUpserted,
  echoSuppressed
} = realtimeSlice.actions

/**
 * The action types the event map's rows resolve to. The reducer-exists test
 * (AC-9) pins this contract: every `EVENT_TYPE_MAP` row must return an action
 * whose type appears here, and a registered reducer must handle it.
 */
export const REALTIME_ACTION_TYPES = {
  invalidateBoard: invalidateBoard.type,
  invalidateProjects: invalidateProjects.type,
  invalidateVersions: invalidateVersions.type,
  invalidateActivity: invalidateActivity.type,
  invalidateNotifications: invalidateNotifications.type,
  shellUpserted: shellUpserted.type,
  echoSuppressed: echoSuppressed.type
} as const

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithRealtime {
  realtime: RealtimeState
}

export const selectRealtimeNonces = (s: WithRealtime) => s.realtime.nonces
export const selectRealtimeEchoes = (s: WithRealtime) => s.realtime.echoes

/** The shell for one id, memoised per key (createSelector, never inline filter). */
export const selectRealtimeShell = createSelector(
  [(s: WithRealtime) => s.realtime.entities, (_s: WithRealtime, id: string) => id],
  (entities, id) => entities[id] ?? null
)
